import { attr, belongsTo, hasMany } from '@ember-data/model';
import { service } from '@ember/service';
import { Type } from '@warp-drive/core-types/symbols';
import BaseModel from 'volta/models/base-model';
import { collectionCmd, resourceCommand } from 'volta/utils/api';
import { indexByFn } from 'volta/utils/array-utils';
import { FrequencyOptions } from 'volta/utils/date-utils';

import type { IResourceName } from 'volta/models/base-model';
import type UserProfile from 'volta/models/user-profile';
import type SessionUserService from 'volta/services/session-user';
import type { IJsonApiQuery } from 'volta/utils/api/jsonapi-types';
import type { TForecastFrequency, TFrequencyOption } from 'volta/utils/date-utils';
import type { TExternalForecastField } from './external-forecast';
import type ForecastProject from './forecast-project';
import type ForecastProjectMlPipeline from './forecast-project-ml-pipeline';
import type {
  ICreateForecastProjectHeaderCmd,
  ICreateForecastProjectRevisionCmd,
  IUpdateForecastProjectHeaderCmd,
  TForecastProjectHeaderPermission,
  TForecastQuantile
} from './types/forecast-projects';

export const ForecastProjectHeaderResourceName: IResourceName = {
  singular: 'forecastProjectHeader',
  plural: 'forecastProjectHeaders'
};

export default class ForecastProjectHeader extends BaseModel {
  @service declare sessionUser: SessionUserService;

  static modelName = 'forecast-project-header';

  // Commands

  static CreateForecastProjectHeader = collectionCmd<
    ICreateForecastProjectHeaderCmd,
    ForecastProjectHeader,
    typeof ForecastProjectHeader
  >('CreateForecastProjectHeader', {
    queryParams: { include: 'contributors,lastRevision,mlPipeline' }
  });

  UpdateForecastProjectHeader = resourceCommand<
    IUpdateForecastProjectHeaderCmd,
    ForecastProjectHeader
  >('UpdateForecastProjectHeader', { query: { include: 'contributors,lastRevision,mlPipeline' } });

  CreateForecastProjectRevision = resourceCommand<
    ICreateForecastProjectRevisionCmd,
    ForecastProjectHeader
  >('CreateForecastProjectRevision', {
    query: { include: 'contributors,lastRevision,mlPipeline' }
  });

  DeleteForecastProjectHeader = resourceCommand<{}, ForecastProjectHeader>(
    'DeleteForecastProjectHeader',
    { query: { include: 'contributors,lastRevision,mlPipeline' } }
  );

  @attr('string')
  declare name: string;

  @attr('string')
  description?: string;

  @attr({ defaultValue: () => ({}) })
  declare skuSelection: IJsonApiQuery['filter'];

  @attr('string', { defaultValue: 'W' })
  declare forecastFrequency: TForecastFrequency;

  @attr('number')
  declare forecastHorizon: number;

  @attr('array')
  forecastQuantiles?: TForecastQuantile[];

  @attr('array')
  forecastTypes?: string[];

  @attr('array')
  defaultGroupBy?: TExternalForecastField[][];

  @attr('string')
  lastRevisionId?: string;

  @attr('array')
  selectedManualSources?: string[];

  @hasMany<UserProfile>('user-profile', { async: false, inverse: null })
  declare contributors: UserProfile[];

  @attr('array', { defaultValue: () => [] })
  declare contributorIds: string[];

  @attr({ defaultValue: () => [] })
  declare permissions: Record<string, TForecastProjectHeaderPermission[]>;

  @attr('number', { defaultValue: 0 })
  declare revisionsCount: number;

  @belongsTo('forecast-project-ml-pipeline', { async: false, inverse: null })
  mlPipeline?: ForecastProjectMlPipeline;

  @belongsTo('forecast-project', { async: false, inverse: null })
  lastRevision?: ForecastProject;

  @attr('string')
  declare createdBy: string;

  @attr('string')
  updatedBy?: string;

  @attr('date')
  deactivatedAt?: Date;

  @attr('number', { defaultValue: 1 })
  declare version: number;

  get forecastFrequencyOption() {
    return ForecastFrequencyOptions[this.forecastFrequency];
  }

  get updateCmd() {
    return {
      name: this.name,
      description: this.description,
      defaultGroupBy: this.defaultGroupBy,
      forecastFrequency: this.forecastFrequency,
      forecastHorizon: this.forecastHorizon,
      forecastQuantiles: this.forecastQuantiles,
      forecastTypes: this.forecastTypes,
      skuSelection: this.skuSelection,
      selectedManualSources: this.selectedManualSources,
      permissions: this.permissions,
      automlEnabled: this.mlPipeline?.scheduleId != null,
      cron: this.mlPipeline?.schedule?.cron
    };
  }

  canUserDo = (action: string) => {
    const permission =
      `ForecastProjectHeaders${action.replace('ForecastProjectHeaders', '')}` as TForecastProjectHeaderPermission;
    return (
      this.sessionUser.user.isStaff ||
      (this.permissions?.[this.sessionUser.userId]?.some(
        (p) => p === 'All' || p === 'ForecastProjectHeadersAll' || p === permission
      ) ??
        false)
    );
  };

  [Type] = 'forecast-project-header' as const;
}

export const ForecastFrequencyOptions: Record<TForecastFrequency, TFrequencyOption> = indexByFn(
  Object.values(FrequencyOptions),
  (o) => o.forecast
);

export const ForecastQuantiles = {
  P10: 0.1,
  P20: 0.2,
  P25: 0.25,
  P30: 0.3,
  P40: 0.4,
  P50: 0.5,
  P60: 0.6,
  P70: 0.7,
  P75: 0.75,
  P80: 0.8,
  P90: 0.9
} as const;
